import type { GettextInterface } from "../../../../../../shared/I18n";

import { Heading, Text, Flex, Box } from "../../../../../../mads";
import { LinkButton } from "../LinkButton";

import { spacing, colors } from "@indeed/ifl-tokens/react-native";
import React from "react";
import { Platform, StyleSheet, Linking } from "react-native";
import Svg, { G, Path } from "react-native-svg";

const LegacyCompanyIcon = () => (
  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
    <G fill="none" fillRule="evenodd">
      <Path fill="#FFD35C" d="M12.108 21.137h5.15v-4.615h-5.15z" />
      <Path fill="#FF5A1F" d="M12 16h5.258v5.659H12z" />
      <Path
        fill="#000"
        d="M7.686 2.515l-.002 5.408H1.667v14.592h20V2.519L7.686 2.515zM9.76 4.587l9.831.002v15.854h-1.7v-4.338h-6.436v4.338h-1.68L9.76 4.587zM3.742 9.995h3.94L7.68 20.443H3.742V9.995zm8.75 10.448v-3.302h4.36v3.302h-4.36zM11.45 10.377v-1h6.448v1H11.45zm0 2.138v-1h6.448v1H11.45z"
      />
    </G>
  </Svg>
);

/**
 * Component to render multiline text with proper line breaks
 */
function MultilineText({ text }: { text: string }) {
  const lines = text.split(/(?:\r\n|\r|\n)/);
  return (
    <>
      {lines.map((line, index) => (
        <Text key={`line-${index}`}>
          {line}
          {index < lines.length - 1 ? "\n" : ""}
        </Text>
      ))}
    </>
  );
}

export interface CompanyDetailJobInsightProps {
  i18n: GettextInterface;
  headquartersLocation?: string;
  employeeRange?: string;
  industry?: string;
  offsiteUrl?: string;
  companyOffsiteClickHandler?: () => void;
  companyName?: string;
}

export function CompanyDetailJobInsight(props: CompanyDetailJobInsightProps) {
  const {
    i18n,
    headquartersLocation,
    employeeRange,
    industry,
    offsiteUrl,
    companyOffsiteClickHandler,
    companyName,
  } = props;

  if (!headquartersLocation && !employeeRange && !industry) {
    return null;
  }

  const listElements = [
    [i18n.gettext("Headquarters"), headquartersLocation],
    [i18n.gettext("Employees"), employeeRange],
    [i18n.gettext("Industry"), industry],
  ].map(([title, value]) => {
    return !value ? null : (
      <Box sx={styles.companyDetailRow} key={`company-section-${title}`}>
        <Heading level={2} sx={{ marginBlockEnd: 0 }}>
          {title}
        </Heading>
        <Text level={2}>{typeof value === "string" ? <MultilineText text={value} /> : value}</Text>
      </Box>
    );
  });

  const shouldShowOffsiteLink = offsiteUrl && offsiteUrl.length > 0;

  const handleOffsiteLinkPress = () => {
    if (companyOffsiteClickHandler) {
      companyOffsiteClickHandler();
    }
    if (Platform.OS !== "web" && offsiteUrl) {
      Linking.openURL(offsiteUrl).catch((err) => {
        console.error("Failed to open URL:", err);
      });
    }
  };

  return (
    <Box sx={styles.companyDetailContainer}>
      <Flex sx={styles.titleContainer}>
        <LegacyCompanyIcon />
        <Heading sx={styles.titleText}>{i18n.gettext("Company details")}</Heading>
      </Flex>
      {listElements}
      {shouldShowOffsiteLink && (
        <LinkButton
          text={
            companyName
              ? i18n.gettext("Learn more about {0}", companyName)
              : i18n.gettext("Learn more about this company")
          }
          href={offsiteUrl}
          hrefAttrs={{ target: "_blank" }}
          onPress={handleOffsiteLinkPress}
          containerStyle={styles.linkContainer}
          textContainerStyle={styles.linkTextContainer}
        />
      )}
    </Box>
  );
}

const styles = StyleSheet.create({
  companyDetailContainer: {
    borderTopWidth: 1,
    borderTopColor: colors.borderWeak,
    paddingTop: spacing.s3,
  },
  titleContainer: {
    alignItems: "center",
    marginVertical: spacing.s4,
    flexDirection: "row",
  },
  titleText: {
    marginLeft: spacing.s2,
    marginVertical: 0,
  },
  companyLogo: {
    width: 24,
    height: 24,
    borderRadius: 4,
    marginRight: spacing.s1,
  },
  companyDetailRow: {
    marginBlockEnd: 2,
  },
  rowTitle: {
    marginBottom: 0,
  },
  linkContainer: {
    marginHorizontal: 0,
  },
  linkTextContainer: {
    marginTop: spacing.s4,
  },
});
